import React, { useState, useRef, useEffect } from 'react';
import { Globe, Search, Check, ChevronDown } from 'lucide-react';

import { useLanguage } from '../context/LanguageContext';

const LanguageSelector = ({ compact = false }) => {
  const { language, setLanguage, languageList, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Filter languages by search
  const filteredLanguages = Object.entries(languageList).filter(([code, info]) => {
    const searchLower = search.toLowerCase();
    return (
      info.name.toLowerCase().includes(searchLower) ||
      info.native.toLowerCase().includes(searchLower) ||
      code.toLowerCase().includes(searchLower)
    );
  });
  
  // Group languages by region for easier navigation
  const groupedLanguages = {
    popular: ['en', 'es', 'fr', 'de', 'pt', 'it', 'zh', 'ja', 'ko', 'ar', 'hi', 'ru'],
    european: ['nl', 'pl', 'uk', 'sv', 'no', 'da', 'fi', 'cs', 'sk', 'hu', 'ro', 'bg', 'hr', 'sr', 'sl', 'el', 'ca', 'eu', 'gl'],
    asian: ['bn', 'pa', 'ta', 'te', 'mr', 'tr', 'vi', 'th', 'id', 'ms', 'fil', 'ne', 'si', 'my', 'km', 'lo', 'mn'],
    middleEast: ['he', 'fa', 'ur'],
    african: ['sw', 'am', 'ha', 'yo', 'ig', 'zu', 'xh', 'af', 'so', 'mg', 'rw'],
    other: []
  };
  
  // Get current language info
  const currentLang = languageList[language] || languageList.en;
  
  if (compact) {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-slate-300 text-sm transition"
          data-testid="language-selector-compact"
        >
          <Globe className="w-4 h-4" />
          <span className="uppercase">{language}</span>
        </button>
        
        {isOpen && (
          <div className="absolute right-0 top-full mt-2 w-64 max-h-80 overflow-y-auto bg-slate-800 border border-slate-700 rounded-xl shadow-xl z-50">
            <div className="p-2 border-b border-slate-700 sticky top-0 bg-slate-800">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('search')}
                  className="w-full pl-8 pr-3 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-400 focus:outline-none focus:border-blue-500"
                  autoFocus
                />
              </div>
            </div>
            
            <div className="p-1">
              {filteredLanguages.map(([code, info]) => (
                <button
                  key={code}
                  onClick={() => {
                    setLanguage(code);
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition ${
                    code === language
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  <span>
                    {info.native}
                    <span className="text-slate-500 ml-2 text-xs">{info.name}</span>
                  </span>
                  {code === language && <Check className="w-4 h-4" />}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-800 border border-slate-700 hover:border-slate-600 text-white transition"
        data-testid="language-selector"
      >
        <Globe className="w-5 h-5 text-blue-400" />
        <span>{currentLang.native}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute left-0 top-full mt-2 w-80 max-h-96 overflow-hidden bg-slate-800 border border-slate-700 rounded-xl shadow-xl z-50">
          {/* Search */}
          <div className="p-3 border-b border-slate-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('selectLanguage')}
                className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
                autoFocus
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">
              100 {t('languages')} supported
            </p>
          </div>
          
          {/* Languages list */}
          <div className="max-h-72 overflow-y-auto p-2">
            {search ? (
              // Show filtered results
              filteredLanguages.length > 0 ? (
                filteredLanguages.map(([code, info]) => (
                  <LanguageOption
                    key={code}
                    code={code}
                    info={info}
                    isSelected={code === language}
                    onSelect={() => {
                      setLanguage(code);
                      setIsOpen(false);
                      setSearch('');
                    }}
                  />
                ))
              ) : (
                <p className="text-center text-slate-500 py-4">No languages found</p>
              )
            ) : (
              // Show grouped languages
              <>
                <div className="mb-2">
                  <p className="text-xs text-slate-500 px-3 py-1 uppercase tracking-wider">Popular</p>
                  {groupedLanguages.popular.map(code => {
                    const info = languageList[code];
                    if (!info) return null;
                    return (
                      <LanguageOption
                        key={code}
                        code={code}
                        info={info}
                        isSelected={code === language}
                        onSelect={() => {
                          setLanguage(code);
                          setIsOpen(false);
                        }}
                      />
                    );
                  })}
                </div>
                
                <div className="border-t border-slate-700 pt-2 mt-2">
                  <p className="text-xs text-slate-500 px-3 py-1 uppercase tracking-wider">All Languages</p>
                  {Object.entries(languageList)
                    .filter(([code]) => !groupedLanguages.popular.includes(code))
                    .sort((a, b) => a[1].name.localeCompare(b[1].name))
                    .map(([code, info]) => (
                      <LanguageOption
                        key={code}
                        code={code}
                        info={info}
                        isSelected={code === language}
                        onSelect={() => {
                          setLanguage(code);
                          setIsOpen(false);
                        }}
                      />
                    ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Individual language option component
const LanguageOption = ({ code, info, isSelected, onSelect }) => (
  <button
    onClick={onSelect}
    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition ${
      isSelected
        ? 'bg-blue-500/20 text-blue-400'
        : 'text-slate-300 hover:bg-slate-700'
    }`}
  >
    <div className="flex items-center gap-3">
      <span className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs uppercase font-mono">
        {code}
      </span>
      <div className="text-left">
        <p className={isSelected ? 'text-blue-400' : 'text-white'}>{info.native}</p>
        <p className="text-slate-500 text-xs">{info.name}</p>
      </div>
    </div>
    {isSelected && <Check className="w-4 h-4 flex-shrink-0" />}
    {info.rtl && (
      <span className="text-xs bg-slate-700 px-1.5 py-0.5 rounded text-slate-400 ml-2">RTL</span>
    )}
  </button>
);

export default LanguageSelector;
